import { useEffect, useState } from "react";
import styled from "styled-components";

import { useCustomerPortal } from "../../context/CustomerPortalContext";
import CustomerHero from "../../components/customer/CustomerHero";
import CustomerNavigation from "../../components/customer/CustomerNavigation";
import CustomerCard from "../../components/customer/CustomerCard";
import {
  fetchCurrentCustomerLoyalty,
  fetchCurrentCustomerLoyaltyBenefits,
  fetchCurrentCustomerLoyaltyTransactions,
} from "../../services/customerPortalApi";

export default function CustomerLoyaltyPage() {
  const { currentCustomer, authLoading, error, fetchCustomer, logout } =
    useCustomerPortal();

  const [loyalty, setLoyalty] = useState(null);
  const [benefits, setBenefits] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loyaltyLoading, setLoyaltyLoading] = useState(false);
  const [loyaltyError, setLoyaltyError] = useState("");

  useEffect(() => {
    if (!currentCustomer) {
      fetchCustomer();
    }
  }, [currentCustomer, fetchCustomer]);

  useEffect(() => {
    let mounted = true;

    async function loadLoyalty() {
      setLoyaltyLoading(true);
      setLoyaltyError("");

      try {
        const [accountResponse, benefitsResponse, transactionsResponse] =
          await Promise.all([
            fetchCurrentCustomerLoyalty(),
            fetchCurrentCustomerLoyaltyBenefits(),
            fetchCurrentCustomerLoyaltyTransactions(),
          ]);

        if (!mounted) return;

        setLoyalty(accountResponse.data || null);
        setBenefits(normalizeList(benefitsResponse.data));
        setTransactions(normalizeList(transactionsResponse.data));
      } catch (err) {
        if (!mounted) return;
        setLoyaltyError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            "Impossible de charger la fidélité client."
        );
      } finally {
        if (mounted) {
          setLoyaltyLoading(false);
        }
      }
    }

    if (currentCustomer) {
      loadLoyalty();
    }

    return () => {
      mounted = false;
    };
  }, [currentCustomer]);

  if (authLoading) {
    return (
      <PageWrapper>
        <PanelInfo>Chargement de votre fidélité...</PanelInfo>
      </PageWrapper>
    );
  }

  if (error && !currentCustomer) {
    return (
      <PageWrapper>
        <PanelMessage $error>{error}</PanelMessage>
      </PageWrapper>
    );
  }

  const account = loyalty?.account || loyalty || {};
  const balance =
    account.balance ??
    account.points ??
    account.available_points ??
    currentCustomer?.loyalty_points ??
    0;

  const progress = account.progress || loyalty?.progress || {};
  const progressCurrent =
    progress.current_amount ?? progress.current ?? account.current_amount ?? 0;
  const progressTarget =
    progress.target_amount ?? progress.target ?? account.next_threshold ?? 100;
  const progressPercent =
    progressTarget > 0
      ? Math.min(100, Math.round((Number(progressCurrent) / Number(progressTarget)) * 100))
      : 0;

  return (
    <PageWrapper>
      <CustomerPanel>
        <CustomerHero customer={currentCustomer} onLogout={logout} />

        <PortalLayout>
          <CustomerNavigation />

          <ContentArea>
            {loyaltyError && <PanelMessage $error>{loyaltyError}</PanelMessage>}

            <Grid>
              <CustomerCard title="Mon compte fidélité">
                {loyaltyLoading ? (
                  <PanelInfo>Chargement...</PanelInfo>
                ) : (
                  <>
                    <MetricBox>
                      <strong>{balance}</strong>
                      <span>points ou avantages disponibles</span>
                    </MetricBox>
                    <MutedText>
                      Vos avantages sont calculés automatiquement après vos achats
                      éligibles.
                    </MutedText>
                  </>
                )}
              </CustomerCard>

              <CustomerCard title="Progression fidélité">
                <ProgressHeader>
                  <strong>{formatAmount(progressCurrent)}</strong>
                  <span>sur {formatAmount(progressTarget)}</span>
                </ProgressHeader>
                <ProgressBar>
                  <ProgressFill style={{ width: `${progressPercent}%` }} />
                </ProgressBar>
                <MutedText>
                  Progression indicative vers votre prochain avantage.
                </MutedText>
              </CustomerCard>

              <CustomerCard title="Mes récompenses" full>
                {benefits.length > 0 ? (
                  <BenefitGrid>
                    {benefits.map((benefit) => (
                      <BenefitCard key={benefit.uuid || benefit.id}>
                        <strong>{benefit.label || benefit.name || "Récompense"}</strong>
                        <span>{benefit.description || benefit.status || "Disponible"}</span>
                      </BenefitCard>
                    ))}
                  </BenefitGrid>
                ) : (
                  <EmptyState>
                    Aucune récompense disponible pour le moment.
                  </EmptyState>
                )}
              </CustomerCard>

              <CustomerCard title="Historique fidélité" full>
                {transactions.length > 0 ? (
                  <TransactionList>
                    {transactions.map((transaction) => (
                      <TransactionItem key={transaction.uuid || transaction.id}>
                        <div>
                          <strong>
                            {transaction.label ||
                              transaction.reason ||
                              transaction.type ||
                              "Mouvement fidélité"}
                          </strong>
                          <span>
                            {transaction.created_at ||
                              transaction.date ||
                              "Date non renseignée"}
                          </span>
                        </div>
                        <TransactionValue>
                          {transaction.amount ?? transaction.points ?? ""}
                        </TransactionValue>
                      </TransactionItem>
                    ))}
                  </TransactionList>
                ) : (
                  <EmptyState>
                    Aucun mouvement fidélité n’est encore enregistré.
                  </EmptyState>
                )}
              </CustomerCard>
            </Grid>
          </ContentArea>
        </PortalLayout>
      </CustomerPanel>
    </PageWrapper>
  );
}

function normalizeList(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.benefits)) return data.benefits;
  if (Array.isArray(data?.transactions)) return data.transactions;
  if (Array.isArray(data?.redemptions)) return data.redemptions;
  return [];
}

function formatAmount(value) {
  const numeric = Number(value || 0);
  return `${numeric.toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  })} €`;
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100vw;
  padding: 1.5rem;
  background: #0b1328;
  box-sizing: border-box;
  overflow-y: auto;
`;

const CustomerPanel = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  color: #f7f7f7;
`;

const PortalLayout = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentArea = styled.div`
  min-width: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MetricBox = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  text-align: center;
  margin-bottom: 1rem;

  strong {
    display: block;
    font-size: 2rem;
    font-weight: 950;
  }

  span {
    font-size: 0.82rem;
    opacity: 0.72;
  }
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.8rem;

  strong {
    font-weight: 950;
  }

  span {
    opacity: 0.72;
  }
`;

const ProgressBar = styled.div`
  height: 14px;
  border-radius: 999px;
  background: #202a43;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: #fff9ea;
`;

const BenefitGrid = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const BenefitCard = styled.article`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;

  strong {
    display: block;
    font-weight: 950;
    margin-bottom: 0.35rem;
  }

  span {
    opacity: 0.74;
  }
`;

const TransactionList = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 0.25rem;
    opacity: 0.66;
    font-size: 0.82rem;
  }
`;

const TransactionValue = styled.strong`
  white-space: nowrap;
`;

const MutedText = styled.p`
  margin: 0;
  opacity: 0.72;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  opacity: 0.75;
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const PanelInfo = styled.div`
  padding: 1rem;
  color: #f7f7f7;
  opacity: 0.75;
`;
